import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private isLoggedInSubject = new BehaviorSubject<boolean>(this.hasToken());
  public isLoggedIn$ = this.isLoggedInSubject.asObservable();

  constructor(private router: Router) {
    // Check token validity on service initialization
    this.checkTokenValidity();
  }

  /**
   * Check if user has a token
   */
  private hasToken(): boolean {
    return !!localStorage.getItem('token');
  }

  /**
   * Set authentication token and update login status
   */
  setToken(token: string): void {
    localStorage.setItem('token', token);
    this.isLoggedInSubject.next(true);
  }

  /**
   * Get current authentication token
   */
  getToken(): string | null {
    return localStorage.getItem('token');
  }

  /**
   * Check if token is valid (not expired)
   * This is a basic check - the real validation happens on the server
   */
  private checkTokenValidity(): void {
    const token = this.getToken();
    if (!token) {
      this.isLoggedInSubject.next(false);
      return;
    }

    try {
      // Check if token is expired by decoding it
      // This is just a client-side check, the server will do the real validation
      const payload = JSON.parse(atob(token.split('.')[1]));
      const isValid = payload.exp > Date.now() / 1000;
      
      if (!isValid) {
        this.logout();
      } else {
        this.isLoggedInSubject.next(true);
      }
    } catch (e) {
      // If token is malformed, log out
      this.logout();
    }
  }

  /**
   * Log out user and clear all session data
   */
  logout(): void {
    // Clear token and user session data from BOTH storages
    localStorage.removeItem('token');
    localStorage.removeItem('loginId');
    localStorage.removeItem('name');
    localStorage.removeItem('type');
    localStorage.removeItem('teamName');
    localStorage.removeItem('assignProcess');
    localStorage.removeItem('addedId');
    sessionStorage.removeItem('loginId');
    sessionStorage.removeItem('name');
    sessionStorage.removeItem('type');
    sessionStorage.removeItem('teamName');
    sessionStorage.removeItem('assignProcess');
    sessionStorage.removeItem('addedId');
    
    // Update login status
    this.isLoggedInSubject.next(false);
    
    // Redirect to login page
    this.router.navigate(['/user-pages/login']);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.hasToken();
  }
}
